import { Controller } from "@hotwired/stimulus"
import * as THREE from "three"
import {
  subscribeSignaling,
  applyRemoteSignal,
  csrfToken,
  newPeerId,
} from "../helpers/beam_signaling"
import { createPanoramaViewer } from "../helpers/panorama_viewer"

// The Live-Ansicht: a watch-only browser peer on a robot (Beam::LiveView).
//
// A deliberately thin cousin of beam_teleop_controller.js. It shares the transport —
// a view session over HTTP, ActionCable signaling (helpers/beam_signaling.js, common to
// both), an RTCPeerConnection with the three POSITIONAL recvonly video m-lines the
// bridge binds by index (main, usb, theta) — and nothing else: no drive commands, no
// captures, no telemetry widgets, no gamepad. The
// data channel exists only for `feed` toggles and the bridge's `feed_status` acks; a
// view-scoped token makes the bridge drop anything else anyway.
//
// Exactly one camera is live at a time. The bridge starts every feed enabled, so on
// every data-channel open we disable all but the selected one; switching a camera
// enables the new feed and disables the old. One feed per watcher is what keeps the
// dock's uplink and the shared encoder's fan-out cheap (see the bridge's
// shared_encoder.py) — and a single fullscreen picture is all a watcher wants.

// Positional binding — the offer's video m-line order. Must stay in this order even
// when a camera is not permitted: the bridge counts slots, it does not read names.
const FEEDS = ["main", "usb", "theta"]

// A lost connection is retried with a fresh view session after these delays; the
// counter resets once a peer connects again. After the last one the page gives up and
// leaves the Verbinden button to the watcher.
const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 20000]

export default class extends Controller {
  static targets = [
    "video",
    "usbVideo",
    "thetaVideo",
    "thetaViewer",
    "tile",
    "cameraButton",
    "placeholder",
    "placeholderText",
    "statusDot",
    "connectionStatus",
    "connectButton",
    "disconnectButton",
  ]

  static values = {
    startUrl: String,
    channel: String,
    robotId: String,
    cameras: Array,
    autostart: Boolean,
    iceServers: Array,
    statusLabels: Object,
  }

  connect() {
    this.pc = null
    this.dataChannel = null
    this.subscription = null
    // This page's identity on the signaling channel (see helpers/beam_signaling.js).
    this.peerId = newPeerId()
    this.sessionId = null
    this.endUrl = null
    this.sessionEnded = true
    this.thetaViewer = null
    this.transceivers = {}
    this.arrived = { main: false, usb: false, theta: false }
    this.reconnectAttempt = 0
    this.reconnectTimer = null
    this.active = this.camerasValue[0] ?? null
    this.applyCameraUi()
    // A closing tab must still release its view session: the cap counts active rows.
    this.onBeforeUnload = () => this.endSessionBeacon()
    window.addEventListener("beforeunload", this.onBeforeUnload)
    document.addEventListener("turbo:before-cache", this.onBeforeUnload)
    if (this.autostartValue) this.start()
  }

  disconnect() {
    this.cancelReconnect()
    window.removeEventListener("beforeunload", this.onBeforeUnload)
    document.removeEventListener("turbo:before-cache", this.onBeforeUnload)
    this.endSessionBeacon()
    this.teardown()
  }

  // --- lifecycle ---

  async start() {
    if (this.pc) return
    this.cancelReconnect()
    this.setStatus("connecting")
    this.showPlaceholder(this.statusLabelsValue.connecting)
    try {
      const session = await this.startSession()
      this.sessionId = session.session_id
      this.endUrl = session.end_url
      this.sessionEnded = false
      this.openPeerConnection()
      // Wait for the subscription to be confirmed before the offer: ActionCable does
      // not replay a `perform` issued while the socket is still connecting.
      await this.subscribeSignaling()
      await this.sendOffer()
      this.toggleButtons(true)
    } catch (error) {
      console.error("[beam-live] start failed", error)
      this.setStatus("failed")
      this.showPlaceholder(error.userMessage ?? this.statusLabelsValue.failed)
      // A start that got as far as a session row but no further (subscription rejected
      // or timed out) must give its seat of the viewer cap back.
      await this.endSession()
      this.teardown()
      // Only a retry keeps retrying; a first start that fails (viewer cap, no permission)
      // is the watcher's to repeat.
      if (this.reconnectAttempt > 0) this.scheduleReconnect()
    }
  }

  async stop() {
    this.cancelReconnect()
    this.reconnectAttempt = 0
    await this.endSession()
    this.teardown()
    this.setStatus("disconnected")
    this.showPlaceholder(this.statusLabelsValue.disconnected)
  }

  // --- HTTP session ---

  async startSession() {
    const response = await fetch(this.startUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
    })
    if (!response.ok) {
      const error = new Error(`start view session failed: ${response.status}`)
      // 409 = viewer cap, 403 = not permitted: both carry a message worth showing.
      try {
        error.userMessage = (await response.json()).error
      } catch {
        /* no JSON body — fall back to the generic label */
      }
      throw error
    }
    return response.json()
  }

  async endSession() {
    if (!this.endUrl || this.sessionEnded) return
    this.sessionEnded = true
    try {
      await fetch(this.endUrl, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
    } catch (error) {
      console.error("[beam-live] end session failed", error)
    }
  }

  // Fire-and-forget for teardown paths (tab close, Turbo navigation): `keepalive`
  // lets the request outlive the unload, nothing awaits it.
  endSessionBeacon() {
    if (!this.endUrl || this.sessionEnded) return
    this.sessionEnded = true
    fetch(this.endUrl, {
      method: "DELETE",
      keepalive: true,
      headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
    }).catch(() => {})
  }

  // --- WebRTC ---

  openPeerConnection() {
    this.pc = new RTCPeerConnection({ iceServers: this.iceServersValue })
    this.dataChannel = this.pc.createDataChannel("commands")
    this.dataChannel.addEventListener("open", () => this.applyFeeds())
    this.dataChannel.addEventListener("message", (event) => this.onDataChannelMessage(event))
    for (const feed of FEEDS) {
      this.transceivers[feed] = this.pc.addTransceiver("video", { direction: "recvonly" })
    }
    this.pc.addEventListener("track", (event) => this.onTrack(event))
    this.pc.addEventListener("icecandidate", (event) => {
      if (event.candidate) this.signal("ice", event.candidate.toJSON())
    })
    this.pc.addEventListener("connectionstatechange", () => {
      const state = this.pc.connectionState
      this.setStatus(state)
      if (state === "connected") this.reconnectAttempt = 0
      if (["failed", "closed", "disconnected"].includes(state)) {
        this.showPlaceholder(this.statusLabelsValue[state])
      }
      // `disconnected` may heal on its own; `failed` never does.
      if (state === "failed") this.onConnectionLost()
    })
  }

  // The peer is gone for good: the ICE path failed, or the cable dropped and the server
  // refused the re-subscription because it ended the view session with the cable. Either
  // way this peer connection can never carry a picture again — tear it down and start
  // over with a fresh view session instead of leaving a dead stage with no Verbinden.
  async onConnectionLost() {
    if (!this.pc) return
    this.teardown()
    await this.endSession()
    this.setStatus("disconnected")
    this.showPlaceholder(this.statusLabelsValue.disconnected)
    this.scheduleReconnect()
  }

  scheduleReconnect() {
    this.cancelReconnect()
    const delay = RECONNECT_DELAYS_MS[this.reconnectAttempt]
    if (delay === undefined) {
      this.reconnectAttempt = 0
      return
    }
    this.reconnectAttempt += 1
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.start()
    }, delay)
  }

  cancelReconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
  }

  // Route by transceiver identity: the bridge relays with replaceTrack, so
  // event.streams may be empty — wrap the track directly.
  //
  // `track` fires for all three recvonly m-lines the moment the answer is applied,
  // long before (or without) any frame: a feed that is disabled, or whose camera has
  // no source yet, arrives as a track just the same. So this only wires the sink;
  // whether a picture is actually flowing is what the track's mute state says —
  // `unmute` = RTP arriving, `mute` = the sender stopped — and that is what toggles
  // the placeholder. Gating on the track event showed a black stage instead of
  // "Warte auf Videobild".
  onTrack(event) {
    if (event.track.kind !== "video") return
    const feed = FEEDS.find((name) => this.transceivers[name] === event.transceiver)
    if (!feed) return
    const track = event.track
    const stream = new MediaStream([track])
    if (feed === "main" && this.hasVideoTarget) this.videoTarget.srcObject = stream
    else if (feed === "usb" && this.hasUsbVideoTarget) this.usbVideoTarget.srcObject = stream
    else if (feed === "theta" && this.hasThetaVideoTarget) this.initThetaViewer(stream)
    else return
    track.addEventListener("unmute", () => this.setFeedArrived(feed, true))
    track.addEventListener("mute", () => this.setFeedArrived(feed, false))
    if (!track.muted) this.setFeedArrived(feed, true)
  }

  setFeedArrived(feed, arrived) {
    this.arrived[feed] = arrived
    if (feed !== this.active || !this.pc) return
    if (arrived) this.hidePlaceholder()
    else this.showPlaceholder(this.placeholderTextTarget.dataset.waiting)
  }

  async sendOffer() {
    const offer = await this.pc.createOffer()
    await this.pc.setLocalDescription(offer)
    this.signal("offer", { type: offer.type, sdp: offer.sdp })
  }

  // --- signaling over ActionCable ---

  subscribeSignaling() {
    const { subscription, connected } = subscribeSignaling(this.channelValue, this.sessionId, {
      peer: this.peerId,
      received: (data) => this.onSignal(data),
      onLost: () => this.onConnectionLost(),
    })
    this.subscription = subscription
    return connected
  }

  signal(type, payload) {
    if (this.subscription) this.subscription.perform("signal", { type, payload })
  }

  async onSignal(data) {
    if (!this.pc) return
    if (data.type === "replaced") {
      if (data.peer !== this.peerId) this.onReplaced()
      return
    }
    await applyRemoteSignal(this.pc, data)
  }

  // Another page of this account took the view session over (the same watcher opened
  // the stage twice). Stop here without ending the session — it is theirs now — and
  // without the automatic reconnect, which would just take it back.
  onReplaced() {
    this.cancelReconnect()
    this.sessionEnded = true
    this.teardown()
    this.setStatus("replaced")
    this.showPlaceholder(this.statusLabelsValue.replaced)
  }

  onDataChannelMessage(event) {
    let data
    try {
      data = JSON.parse(event.data)
    } catch {
      return
    }
    if (data.type !== "feed_status") return
    const { feed, available } = data.payload || {}
    // A camera with no source on this robot: take its tab away rather than show a
    // placeholder forever. Falls back to the first camera that is still there.
    if (available === false) this.dropCamera(feed)
  }

  // --- cameras ---

  selectCamera(event) {
    const camera = event.currentTarget.dataset.camera
    if (!this.camerasValue.includes(camera) || camera === this.active) return
    this.active = camera
    this.applyCameraUi()
    this.applyFeeds()
    if (this.arrived[camera]) this.hidePlaceholder()
    else if (this.pc) this.showPlaceholder(this.placeholderTextTarget.dataset.waiting)
  }

  // Exactly one feed enabled: the active camera. Sent on every data-channel open
  // (the bridge starts all feeds on) and on every switch.
  applyFeeds() {
    if (!this.dataChannel || this.dataChannel.readyState !== "open") return
    for (const feed of FEEDS) {
      const enable = feed === this.active && this.camerasValue.includes(feed)
      this.dataChannel.send(
        JSON.stringify({ type: "feed", payload: { feed, action: enable ? "enable" : "disable" } }),
      )
    }
  }

  applyCameraUi() {
    this.tileTargets.forEach((tile) => {
      tile.hidden = tile.dataset.feed !== this.active
    })
    this.cameraButtonTargets.forEach((button) => {
      button.setAttribute("aria-selected", button.dataset.camera === this.active ? "true" : "false")
    })
    // The sphere viewer sizes itself to its container; it needs a nudge when unhidden.
    if (this.active === "theta") window.dispatchEvent(new Event("resize"))
  }

  dropCamera(feed) {
    this.cameraButtonTargets.forEach((button) => {
      if (button.dataset.camera === feed) button.remove()
    })
    this.camerasValue = this.camerasValue.filter((camera) => camera !== feed)
    if (this.active === feed) {
      this.active = this.camerasValue[0] ?? null
      this.applyCameraUi()
      this.applyFeeds()
    }
  }

  // --- Theta 360 (shared equirectangular sphere viewer) ---

  initThetaViewer(stream) {
    const video = this.thetaVideoTarget
    video.srcObject = stream
    video.muted = true
    video.playsInline = true
    const playing = video.play()
    if (playing) playing.catch(() => {})
    if (this.thetaViewer || !this.hasThetaViewerTarget) return
    const texture = new THREE.VideoTexture(video)
    texture.colorSpace = THREE.SRGBColorSpace
    this.thetaViewer = createPanoramaViewer(this.thetaViewerTarget, { texture })
  }

  // --- teardown & UI ---

  teardown() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    if (this.dataChannel) {
      this.dataChannel.close()
      this.dataChannel = null
    }
    if (this.pc) {
      this.pc.close()
      this.pc = null
    }
    if (this.thetaViewer) {
      this.thetaViewer.destroy()
      this.thetaViewer = null
    }
    for (const target of ["video", "usbVideo", "thetaVideo"]) {
      const name = `has${target[0].toUpperCase()}${target.slice(1)}Target`
      if (this[name]) this[`${target}Target`].srcObject = null
    }
    this.transceivers = {}
    this.arrived = { main: false, usb: false, theta: false }
    this.toggleButtons(false)
  }

  toggleButtons(connected) {
    if (this.hasConnectButtonTarget) this.connectButtonTarget.hidden = connected
    if (this.hasDisconnectButtonTarget) this.disconnectButtonTarget.hidden = !connected
  }

  setStatus(state) {
    if (this.hasConnectionStatusTarget)
      this.connectionStatusTarget.textContent = this.statusLabelsValue[state] ?? state
    if (!this.hasStatusDotTarget) return
    const tone =
      { connected: "bg-green-400", connecting: "bg-yellow-400", failed: "bg-red-400" }[state] ??
      "bg-gray-400"
    this.statusDotTarget.className = `h-1.5 w-1.5 rounded-full ${tone}`
  }

  showPlaceholder(text) {
    if (!this.hasPlaceholderTarget) return
    this.placeholderTarget.hidden = false
    if (text && this.hasPlaceholderTextTarget) this.placeholderTextTarget.textContent = text
  }

  hidePlaceholder() {
    if (this.hasPlaceholderTarget) this.placeholderTarget.hidden = true
  }
}
